<template>
  <div class="page-plans">
    <PageHeader>
      <div class="heading">
        <h1>
          <router-link :to="{name: 'billing'}">Billing Central</router-link> /
          Plans
        </h1>
      </div>
    </PageHeader>

    <div class="page-columns" v-if="currentUser.owns_company">
      <div class="col main">
        <div>
          <div v-if="hasSites">
            <div class="plan-links">
              <router-link
                class="plan-link"
                v-for="site in sites"
                :key="site.id"
                :to="{
                  name: 'plan',
                  params: {
                    companyId: companyId,
                    siteId: site.id
                  }
                }"
              >
                <h2 class="site-name">{{ site.name }}</h2>

                <SubscriptionInfo
                  simpleMode
                  :companyId="companyId"
                  :siteId="site.id"
                />

                <Btn class="view-info" subtle>View info</Btn>
              </router-link>
            </div>
          </div>

          <div class="plan-links" v-else>
            <p>You don't have any plans as you haven't created a website yet.</p>
          </div>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Plans</h1>
            <p>
              Each website you create will be linked with a plan. By default, you'll be added to our <strong>monthly</strong> plan and benefit from a 14 day free trial.
            </p>
            <p>
              During the free trial, you can choose to end the trial early and proceed with the monthly plan, or change to the annual plan.
            </p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view v-if="currentUser.owns_company" />
  </div>
</template>

<script>
import {loadStripe} from '@stripe/stripe-js'
import {mapState} from 'vuex'

import {readSiteSubscriptionData} from '@/lib/payments/subscriptions'
import {readStripeCustomerData} from '@/lib/payments/customers'

import {readCompanyData} from '@/lib/companies'

import {readCompanySitesData} from '@/lib/sites'

import page from '@/mixins/page'

import PageHeader from '@/components/PageHeader'
import Btn from '@/components/Btn'
import PageSummary from '@/components/PageSummary'
import SubscriptionInfo from '@/components/SubscriptionInfo'

export default {
  name: 'plans',

  data() {
    return {
      title: 'Plans',
      stripe: {},
      sites: [],
      customer: {},
      subscription: {},
      company: {},
      portal: {},
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
    }
  },

  mixins: [page],

  props: {
    companyId: String
  },

  components: {
    PageHeader,
    PageSummary,
    SubscriptionInfo,
    Btn
  },

  computed: {
    ...mapState(['currentUser']),

    hasSites() {
      return this.sites.length > 0
    }
  },

  methods: {
    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readStripeCustomerData(this.companyId)
        .then(({data}) => {
          this.customer = data
        })
        .catch((response) => {
          alert(`Ooops! We couldn't seem to find you in our database. Please contact support and we'll see what's up`)
          this.$router.push({name: 'billing'})
        })

      readCompanySitesData(this.companyId, {page: 1, page_size: 250})
        .then(({data}) => {
          this.sites = data
        })
        .catch(() => {

        })
    },

    getSiteSubscription(siteId) {
      readSiteSubscriptionData(this.companyId, siteId)
        .then(({data}) => {
          this.subscription = data
        })
        .catch(({response}) => {

        })
    }
  },

  mounted() {
    loadStripe(this.publishableKey).then((result) => {
      this.stripe = result
    })

    this.getData()
  },

  watch: {
    '$route'() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.page-plans
  @extend %page

  .main > div
    width: 100%

  .subscription-status
    position: absolute
    left: 15px
    top: 10px
    transform: translateX(-50%)
    width: 10px
    height: 10px
    transition: opacity .2s ease-in-out
    opacity: 0.4

    .sprite
      fill: rgba(255, 255, 255, 0.5)
      position: relative
      top: 6px
      left: -1px
      width: 13px
      height: 13px

  .plan-status-message
    opacity: 0.25
    position: absolute
    bottom: $gap-small
    right: $gap-small
    text-transform: uppercase
    font-size: 10px
    transition: opacity .2s ease-in-out

  .view-info
    margin: $gap-small 0

.plan-links
  width: 100%
  padding: $gap-medium 0 $gap-medium $gap-medium
  display: flex
  flex-wrap: wrap

  .btn
    font-size: 10px

  .plan-link
    @extend %card
    flex-basis: calc(#{percentage(1/3)} - #{$gap-small*2})
    padding: $gap-large
    margin: $gap-small $gap-small $gap-large
    display: flex
    align-items: center
    flex-direction: column
    font-size: 14px

    &:hover
      .subscription-status,
      .plan-status-message,
      .subscription-cancel-note,
      .plan-price
        opacity: 1

      .plan-status-message
        &.is-active
          color: $color-active

        &.is-trialing
          color: $color-trialing

        &.is-canceled
          color: $color-canceled

    @include themify($themes)
      color: themed('color-secondary')

  .site-name
    font-size: 12px
    text-align: center
</style>
